import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ShoppingCart, Package } from "lucide-react";

const MySwal = withReactContent(Swal);
const API_BASE_URL = "https://backend.dlapakistan.com/api";

const AllProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToBasket, setAddingToBasket] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [memberToken, setMemberToken] = useState(null);

  const GREEN = "#70B444";

  // Get CSRF token
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') return value;
    }
    return '';
  };

  // Create headers
  const createHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    };
    if (memberToken) headers['Authorization'] = `Bearer ${memberToken}`;
    return headers;
  };

  useEffect(() => {
    const token = localStorage.getItem("member_token");
    setIsMember(!!token);
    setMemberToken(token);
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/dla-shop/products/`);
      const data = await response.json();
      
      let productsData = Array.isArray(data) ? data : data.results || [];
      
      setProducts(productsData.filter(p => p.is_published !== false));
    } catch (err) {
      setError("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToBasket = async (product, e) => {
    e.stopPropagation();
    
    // Check if user is a member for free products
    if (product.is_free_for_members && !isMember) {
      const result = await MySwal.fire({
        title: "Member Exclusive",
        html: `<p class="mb-4">This product is <span class="font-bold text-[#70B444]">FREE</span> for members only!</p>`,
        icon: "info",
        confirmButtonColor: GREEN,
        confirmButtonText: "Login as Member",
        showCancelButton: true,
        cancelButtonText: "Continue as Guest"
      });
      
      if (result.isConfirmed) {
        navigate("/member-login");
      }
      return;
    }

    if (parseInt(product.amount) <= 0) {
      MySwal.fire({ 
        title: "Out of Stock", 
        text: "Product is out of stock", 
        icon: "error", 
        confirmButtonColor: GREEN 
      });
      return;
    }

    setAddingToBasket(product.id);
    
    try {
      const response = await fetch(`${API_BASE_URL}/dla-shop/basket/add_item/`, {
        method: 'POST',
        credentials: 'include',
        headers: createHeaders(),
        body: JSON.stringify({ product_id: product.id, quantity: 1 })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add to basket');
      }
      
      setProducts(prev => prev.map(p => 
        p.id === product.id ? { ...p, amount: p.amount - 1 } : p
      ));
      
      window.dispatchEvent(new CustomEvent('basketUpdated'));
      
      // Show different message for free products
      if (isMember && product.is_free_for_members) {
        MySwal.fire({
          title: "Added to Cart!",
          text: "Free product added to your cart",
          icon: "success",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        MySwal.fire({
          title: "Added to Cart!",
          icon: "success",
          timer: 1500,
          showConfirmButton: false
        });
      }
      
    } catch (error) {
      MySwal.fire({ 
        title: "Error", 
        text: error.message, 
        icon: "error", 
        confirmButtonColor: GREEN 
      });
    } finally {
      setAddingToBasket(null);
    }
  };

  const handleViewDetails = (product) => {
    navigate(`/dlashop/product/${product.id}`);
  };

  const formatPrice = (price) => `$${parseFloat(price || 0).toFixed(2)}`;

  useEffect(() => {
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#70B444]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={fetchProducts} 
          className="bg-[#70B444] text-white px-4 py-2 rounded hover:bg-[#5A9437]"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-gray-900 text-center mb-4">All Products</h1>
      <p className="text-gray-600 text-center mb-16">
        {isMember 
          ? "Welcome back! Member discounts applied at checkout" 
          : "Browse our products - Members get exclusive FREE items!"}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((product) => {
          const isOutOfStock = parseInt(product.amount) <= 0;
          const isFreeForMember = product.is_free_for_members;
          const actualPrice = isMember && isFreeForMember ? 0 : parseFloat(product.price || 0);
          
          return (
            <div 
              key={product.id} 
              className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer relative flex flex-col h-full group"
              onClick={() => handleViewDetails(product)}
            >
              {/* Product Badges */}
              <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                {isFreeForMember && (
                  <span className={`px-3 py-1.5 text-white text-xs font-bold rounded-full ${
                    isMember ? 'bg-[#70B444]' : 'bg-purple-600'
                  }`}>
                    {isMember ? "FREE for Members" : "Member Exclusive"}
                  </span>
                )}
              </div>

              {/* Stock Badge */}
              {isOutOfStock && (
                <div className="absolute top-4 right-4 z-10">
                  <span className="bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold">
                    Out of Stock
                  </span>
                </div>
              )}

              {/* Product Image */}
              <div className="h-48 bg-gray-100 flex items-center justify-center p-4 overflow-hidden">
                {product.thumbnail_url ? (
                  <img 
                    src={product.thumbnail_url} 
                    alt={product.title} 
                    className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <Package className="w-16 h-16 text-gray-400" />
                )}
              </div>
              
              {/* Product Info */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 min-h-[56px]">
                  {product.title}
                </h3>
                
                {/* Price Section */}
                <div className="mb-4">
                  {isMember && isFreeForMember ? (
                    <div>
                      <p className="text-2xl font-bold text-[#70B444]">FREE</p>
                      <p className="text-sm text-gray-400 line-through">
                        {formatPrice(product.price)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-[#70B444]">
                      {formatPrice(product.price)}
                    </p>
                  )}
                  
                  <p className="text-sm text-gray-500 mt-1">
                    {isOutOfStock ? (
                      <span className="text-red-600 font-medium">Out of stock</span>
                    ) : (
                      <span>{product.amount} unit{product.amount !== 1 ? 's' : ''} in stock</span>
                    )}
                  </p>
                </div>

                {/* Action Button */}
                <div className="mt-auto">
                  <button 
                    onClick={(e) => handleAddToBasket(product, e)}
                    disabled={isOutOfStock || addingToBasket === product.id}
                    className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                      isOutOfStock 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : isMember && isFreeForMember
                          ? 'bg-[#70B444] text-white hover:bg-[#5A9437] hover:shadow-md'
                          : 'bg-[#70B444] text-white hover:bg-[#5A9437] hover:shadow-md'
                    }`}
                  >
                    {addingToBasket === product.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5" />
                        {isOutOfStock 
                          ? 'Out of Stock' 
                          : isMember && isFreeForMember 
                            ? 'Get for FREE' 
                            : 'Add to Cart'}
                      </>
                    )}
                  </button>

                  {/* Member Login Prompt for Exclusive Items */}
                  {!isMember && isFreeForMember && (
                    <p className="text-xs text-center mt-3 text-purple-600">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/member-login');
                        }}
                        className="hover:underline font-medium"
                      >
                        Login as member
                      </button> to get this for FREE
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default AllProducts;